import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    DataTable,
    Notification,
    Pagination,
    Tag,
    Text,
    TextInput,
} from 'grommet';
import {
    API,
    CadiCurriculumService,
    RowsPerPageSelect,
    SearchSelect,
    Utils,
    usePaginatedSort,
    resolveSortField,
} from '@enrich/shared-lib';
import { useLoaderData } from 'react-router-dom';
import QueryString from 'qs';

const cadiCurriculumService = new CadiCurriculumService();

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const SEARCH_DEBOUNCE_MS = 300;

const SORT_FIELD_MAP = {
    title: 'title',
    'company.name': 'company.name',
};

function buildJobSearchFilter(search) {
    const term = (search ?? '').trim();
    if (!term) return null;
    return {
        $or: [
            { title: { $containsi: term } },
            { company: { name: { $containsi: term } } },
        ],
    };
}

async function loadJobs({ search, page, pageSize, sort }) {
    const searchFilter = buildJobSearchFilter(search);
    const query = QueryString.stringify(
        {
            filters: searchFilter ? { $and: [searchFilter] } : undefined,
            populate: {
                company: { fields: ['id', 'name'] },
                cadiCurriculums: {
                    fields: ['id', 'cadiId', 'code', 'title', 'isActive'],
                },
            },
            pagination: { page, pageSize },
            sort: [
                `${resolveSortField(sort, SORT_FIELD_MAP, 'title')}:${sort?.direction ?? 'asc'}`,
            ],
        },
        { encodeValuesOnly: true },
    );

    try {
        const response = await API.get(`/api/jobs?${query}`);
        return { ...response.data, error: false };
    } catch (error) {
        console.log(error);
        return {
            data: [],
            meta: { pagination: { page: 1, total: 0 } },
            error: true,
        };
    }
}

const unassignedCurriculums = (job, allCurriculums) =>
    allCurriculums.filter(
        (curriculum) =>
            !(job.cadiCurriculums ?? []).some(
                (assigned) => assigned.id === curriculum.id,
            ),
    );

// CADI allows only one active curriculum per job.
const hasActiveCurriculum = (job) =>
    (job.cadiCurriculums ?? []).some(({ isActive }) => isActive);

const CurriculumSelect = ({ job, curriculums, onAssign }) => {
    const [resetCount, setResetCount] = useState(0);
    const options = useMemo(
        () => unassignedCurriculums(job, curriculums),
        [job, curriculums],
    );

    const handleAssign = async (curriculum) => {
        const assigned = await onAssign(job, curriculum);
        if (!assigned) setResetCount((count) => count + 1);
    };

    return (
        <SearchSelect
            key={`${options.map(({ id }) => id).join(',')}:${resetCount}`}
            placeholder="Select a curriculum"
            labelKey="title"
            valueKey="id"
            options={options}
            updateFormData={handleAssign}
            dropAlign={{ top: 'bottom', right: 'right' }}
        />
    );
};

export const CadiCurriculums = () => {
    const { curriculums: initialCurriculums, error: initialCurriculumsError } =
        useLoaderData();
    const [curriculums, setCurriculums] = useState(
        Utils.deepFlatten(initialCurriculums) ?? [],
    );
    const [syncing, setSyncing] = useState(false);
    const [errorMessages, setErrorMessages] = useState({
        curriculums: initialCurriculumsError
            ? 'Could not load curriculum options. Try again.'
            : null,
        jobs: null,
        action: null,
    });

    const [jobs, setJobs] = useState([]);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const searchDebounceRef = useRef(null);
    const {
        page,
        pageSize,
        sort,
        totalItems,
        stepOptions,
        setPage,
        handleSort,
        handlePageChange,
        handlePageSizeChange,
        applyPagination,
    } = usePaginatedSort({
        initialSort: { property: 'title', direction: 'asc' },
        stepOptions: PAGE_SIZE_OPTIONS,
    });
    const errorMessage =
        errorMessages.action ?? errorMessages.jobs ?? errorMessages.curriculums;
    const dataTableSort = useMemo(() => ({ ...sort, external: true }), [sort]);

    useEffect(() => {
        let isActive = true;

        (async () => {
            const response = await loadJobs({
                search,
                page,
                pageSize,
                sort,
            });

            if (isActive) {
                setJobs(Utils.deepFlatten(response.data) ?? []);
                applyPagination(response.meta);
                setErrorMessages((prev) => ({
                    ...prev,
                    jobs: response.error
                        ? 'Could not load jobs. Try again.'
                        : null,
                }));
            }
        })();

        return () => {
            isActive = false;
        };
    }, [search, page, pageSize, sort, applyPagination]);

    useEffect(() => {
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, []);

    const handleSearchChange = (value) => {
        setSearchInput(value);
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        searchDebounceRef.current = setTimeout(() => {
            setPage(1);
            setSearch(value);
        }, SEARCH_DEBOUNCE_MS);
    };

    const handleSync = async () => {
        setSyncing(true);
        setErrorMessages((prev) => ({ ...prev, action: null }));
        try {
            const synced = await cadiCurriculumService.sync();
            if (!synced) {
                setErrorMessages((prev) => ({
                    ...prev,
                    action: 'Could not sync curriculum options. Try again.',
                }));
                return;
            }

            const refreshed = await cadiCurriculumService.list();
            if (!refreshed) {
                setErrorMessages((prev) => ({
                    ...prev,
                    action: 'Could not load curriculum options. Try again.',
                }));
                return;
            }
            setCurriculums(Utils.deepFlatten(refreshed) ?? []);
        } catch (error) {
            setErrorMessages((prev) => ({
                ...prev,
                action: 'Could not sync curriculum options. Try again.',
            }));
        } finally {
            setSyncing(false);
        }
    };

    const handleAssign = async (job, curriculum) => {
        try {
            const { data, error } = await cadiCurriculumService.addJobMapping(
                curriculum.id,
                job.id,
            );
            if (!data) {
                setErrorMessages((prev) => ({
                    ...prev,
                    action: error ?? 'Could not assign curriculum. Try again.',
                }));
                return false;
            }
            setJobs((prev) =>
                prev.map((j) =>
                    j.id === job.id
                        ? {
                              ...j,
                              cadiCurriculums: [
                                  ...(j.cadiCurriculums ?? []),
                                  // Options come from list(), which only
                                  // returns active curriculums.
                                  { ...curriculum, isActive: true },
                              ],
                          }
                        : j,
                ),
            );
            setErrorMessages((prev) => ({ ...prev, action: null }));
            return true;
        } catch (error) {
            setErrorMessages((prev) => ({
                ...prev,
                action: 'Could not assign curriculum. Try again.',
            }));
            return false;
        }
    };

    const handleUnassign = async (job, curriculum) => {
        try {
            const updated = await cadiCurriculumService.removeJobMapping(
                curriculum.id,
                job.id,
            );
            if (!updated) {
                setErrorMessages((prev) => ({
                    ...prev,
                    action: 'Could not remove curriculum. Try again.',
                }));
                return;
            }
            setJobs((prev) =>
                prev.map((j) =>
                    j.id === job.id
                        ? {
                              ...j,
                              cadiCurriculums: (j.cadiCurriculums ?? []).filter(
                                  (c) => c.id !== curriculum.id,
                              ),
                          }
                        : j,
                ),
            );
            setErrorMessages((prev) => ({ ...prev, action: null }));
        } catch (error) {
            setErrorMessages((prev) => ({
                ...prev,
                action: 'Could not remove curriculum. Try again.',
            }));
        }
    };

    const dataColumns = [
        {
            header: 'Job Title',
            property: 'title',
            primary: true,
            sortable: true,
            size: 'medium',
        },
        {
            header: 'Company',
            property: 'company.name',
            sortable: true,
            render: (job) => job.company?.name,
            size: 'small',
        },
        {
            header: 'Assigned Curriculums',
            property: 'cadiCurriculums',
            size: 'large',
            render: (job) => (
                <Box direction="row" wrap gap="xsmall">
                    {(job.cadiCurriculums ?? []).map((curriculum) => (
                        <Tag
                            key={curriculum.id}
                            value={curriculum.title}
                            onRemove={() => handleUnassign(job, curriculum)}
                        />
                    ))}
                </Box>
            ),
        },
        {
            header: 'Assign Curriculum',
            property: 'assign',
            size: 'medium',
            render: (job) =>
                hasActiveCurriculum(job) ? (
                    <Text size="small" color="text-weak">
                        This job already has a curriculum
                    </Text>
                ) : (
                    <CurriculumSelect
                        job={job}
                        curriculums={curriculums}
                        onAssign={handleAssign}
                    />
                ),
        },
    ];

    return (
        <Box pad={{ top: 'small' }} gap="medium">
            <Box direction="row" justify="between" align="center">
                <Text weight="bold">CADI Curriculums</Text>
                <Button
                    label="Sync curriculums"
                    onClick={handleSync}
                    busy={syncing}
                    disabled={syncing}
                />
            </Box>
            <TextInput
                placeholder="Search by job title or company"
                value={searchInput}
                onChange={(event) => handleSearchChange(event.target.value)}
            />
            <Box overflow="auto">
                <DataTable
                    primaryKey="id"
                    sort={dataTableSort}
                    onSort={handleSort}
                    border="bottom"
                    data={jobs}
                    columns={dataColumns}
                />
            </Box>
            {errorMessage && (
                <Notification
                    toast={{ autoClose: true, position: 'top' }}
                    status="critical"
                    title="Request failed"
                    message={errorMessage}
                    onClose={() =>
                        setErrorMessages({
                            curriculums: null,
                            jobs: null,
                            action: null,
                        })
                    }
                />
            )}
            <Box align="center" justify="center" pad="small">
                <RowsPerPageSelect
                    options={stepOptions}
                    value={pageSize}
                    onChange={handlePageSizeChange}
                />
                <Pagination
                    numberItems={totalItems}
                    page={page}
                    onChange={handlePageChange}
                    step={pageSize}
                    summary={true}
                />
            </Box>
        </Box>
    );
};

export async function loader() {
    const curriculums = await cadiCurriculumService.list();
    return { curriculums: curriculums ?? [], error: curriculums === null };
}
